import React, { FC, useState, useCallback, useMemo } from "react"
import { LoanOfficer } from "../../../../types"
import { LOMap, LOMapEntry } from "../../../../types/pricing-engine"
import { PrimaryBtn } from "../../buttons"
import Select from "../../form/select"
import { UserPlus, Trash2, Pencil, Settings2 } from "lucide-react"
import LOEntryEditor from "./lo-entry-editor"

interface Props {
  currentLoMap: LOMap
  members: LoanOfficer[]
  onChange: (newLoMap: LOMap) => void
}

/**
 * General LO Map Manager for all pricing engines.
 * Allows selecting members and configuring per-LO defaults/raw_defaults.
 * For Optimal Blue specific features (OB LO search, sync), use OBLOMapManager.
 */
const LOMapManager: FC<Props> = ({ currentLoMap, members, onChange }) => {
  const [selectedMemberId, setSelectedMemberId] = useState<string>("")
  const [editingLoid, setEditingLoid] = useState<string | null>(null)

  // Helper to get member's primary ID (prefer loid, fallback to uid)
  const getMemberId = (m: LoanOfficer): string => m.loid || m.uid || ""

  // Helper to check if a member matches an ID (could be loid, uid, or nid)
  const memberMatchesId = (m: LoanOfficer, id: string): boolean =>
    String(m.loid) === String(id) ||
    String(m.uid) === String(id) ||
    String(m.nid) === String(id)

  // Filter members that are not already in the lo_map
  const availableMembers = useMemo(() => {
    const mappedIds = Object.keys(currentLoMap)

    return members.filter(m => {
      const memberId = getMemberId(m)
      if (!memberId) return false

      const isMapped = mappedIds.some(id => memberMatchesId(m, id))
      return !isMapped
    })
  }, [members, currentLoMap])

  const memberOptions = useMemo(
    () =>
      availableMembers.map(m => ({
        id: getMemberId(m),
        name: `${m.first_name} ${m.last_name} (${m.email})`
      })),
    [availableMembers]
  )

  const tableData = useMemo(() => {
    return Object.entries(currentLoMap).map(([loid, entry]) => {
      const member = members.find(m => memberMatchesId(m, loid))

      const defaultsCount = Object.keys(entry.defaults || {}).length
      const rawDefaultsCount = Object.keys(entry.raw_defaults || {}).length
      const hasOverrides = defaultsCount > 0 || rawDefaultsCount > 0

      return {
        loid,
        entry,
        memberName: member
          ? `${member.first_name} ${member.last_name}`
          : `Unknown (${loid})`,
        memberEmail: member?.email || "",
        hasOverrides,
        defaultsCount,
        rawDefaultsCount
      }
    })
  }, [currentLoMap, members])

  const handleAddMapping = useCallback(() => {
    if (!selectedMemberId) return

    const entry: LOMapEntry = {}
    const newLoMap = { ...currentLoMap, [selectedMemberId]: entry }
    onChange(newLoMap)
    setSelectedMemberId("")
  }, [selectedMemberId, currentLoMap, onChange])

  const handleDeleteMapping = useCallback(
    (loid: string) => {
      const newLoMap = { ...currentLoMap }
      delete newLoMap[loid]
      onChange(newLoMap)
      if (editingLoid === loid) {
        setEditingLoid(null)
      }
    },
    [currentLoMap, onChange, editingLoid]
  )

  const handleUpdateEntry = useCallback(
    (loid: string, entry: LOMapEntry) => {
      const newLoMap = { ...currentLoMap, [loid]: entry }
      onChange(newLoMap)
    },
    [currentLoMap, onChange]
  )

  return (
    <div className="space-y-6">
      {/* Add New LO Section */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-4">Add LO to Map</h4>
        <p className="text-sm text-gray-600 mb-4">
          Select a loan officer to configure LO-specific default values for
          pricing requests.
        </p>

        <div className="flex gap-4 items-end">
          <div className="flex-1 max-w-md">
            {memberOptions.length > 0 ? (
              <Select
                label="Select Loan Officer"
                name="member"
                data={memberOptions}
                selected={selectedMemberId}
                setSelected={(value: string) => setSelectedMemberId(value)}
              />
            ) : (
              <div>
                <label className="block font-medium">Select Loan Officer</label>
                <div className="mt-1 text-sm text-amber-600 py-2">
                  All members are already mapped
                </div>
              </div>
            )}
          </div>

          <PrimaryBtn
            onClick={handleAddMapping}
            disabled={!selectedMemberId}
            className="flex items-center"
          >
            <UserPlus size={16} className="mr-2" />
            Add to Map
          </PrimaryBtn>
        </div>
      </div>

      {/* Current Mappings */}
      <div>
        <h4 className="font-medium mb-4">
          LO Mappings ({Object.keys(currentLoMap).length})
        </h4>

        {tableData.length === 0 ? (
          <div className="text-gray-500 text-center py-8 bg-gray-50 rounded-lg">
            No LO mappings configured. Use the form above to add mappings.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Loan Officer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Defaults
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tableData.map(row => (
                  <React.Fragment key={row.loid}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {row.memberName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {row.memberEmail}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {row.hasOverrides ? (
                          <div className="flex items-center gap-1.5">
                            <Settings2 size={14} className="text-primary-500" />
                            <span className="text-xs text-gray-600">
                              {row.defaultsCount > 0 && (
                                <span className="text-primary-600">
                                  {row.defaultsCount} BB
                                </span>
                              )}
                              {row.defaultsCount > 0 &&
                                row.rawDefaultsCount > 0 &&
                                ", "}
                              {row.rawDefaultsCount > 0 && (
                                <span className="text-amber-600">
                                  {row.rawDefaultsCount} Raw
                                </span>
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setEditingLoid(
                                editingLoid === row.loid ? null : row.loid
                              )
                            }
                            className={`p-1 rounded ${
                              editingLoid === row.loid
                                ? "text-primary-600 bg-primary-50"
                                : "text-gray-500 hover:text-primary-600 hover:bg-gray-100"
                            }`}
                            title="Edit defaults"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteMapping(row.loid)}
                            className="text-red-600 hover:text-red-800 p-1"
                            title="Delete mapping"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Inline Editor */}
                    {editingLoid === row.loid && currentLoMap[row.loid] && (
                      <tr>
                        <td colSpan={3} className="p-4 bg-gray-50">
                          <LOEntryEditor
                            entry={currentLoMap[row.loid]}
                            onChange={entry =>
                              handleUpdateEntry(row.loid, entry)
                            }
                            onClose={() => setEditingLoid(null)}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default LOMapManager
